// Normalises a page's formMarkup into a flat list of draggable elements and
// writes dragged/nudged/resized positions back into the same formMarkup shape.
// The stored structure is never changed incompatibly — only `left`/`top`,
// per-field `fontSize`, signature `width`/`height`, and matrix gaps are added or
// updated — so every downstream consumer keeps working (all new props are
// optional and default to the previous behaviour in overlayInfo).

import { rebuildMatrix } from '../../matrixItems';
import { columnWidthsOf, rowHeightsOf, scaleSizes, roundSizes, totalOf } from '../../matrixGrid';
import { SIGNATURE, DEFAULT_MAX_LINES, fieldFontPt } from '../thresholdConstants';
// Dependency-free module — no import cycle.
import { resolveStaticValue } from './staticFieldOptions';
import { DEFAULT_FONT_PT, SIGNATURE_H_MM, SIGNATURE_W_MM, roundMm } from './coords';

export type FieldKind = 'field' | 'signature' | 'checkbox' | 'matrix';

export interface BuilderElement {
  id: string; // unique on the page: key, or `${key}#${itemIndex}` for checkbox items
  key: string; // the formMarkup field key
  itemIndex?: number; // checkbox item index
  kind: FieldKind;
  anchor: 'baseline' | 'topleft';
  leftMm: number;
  topMm: number;
  fontPt: number; // text/checkbox/matrix cell font size (pt)
  wMm?: number; // signature footprint (mm)
  hMm?: number;
  cols?: number; // matrix only
  colWidthsMm?: number[]; // matrix only: width of each column
  rowHeightsMm?: number[]; // matrix only: height of each row
  rows?: number;
  gapHMm?: number;
  gapVMm?: number;
  isRadio?: boolean; // checkbox group only
  multiline?: boolean; // text field wraps in the PDF instead of shrinking
  maxLines?: number;
  label: string;
  text: string;
}

const num = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// A field's own size wins; otherwise the form's global size, which the
// builder toolbar sets and every field inherits.
const fontOf = (item: any, globalPt: number): number => fieldFontPt(item, globalPt);

export const elementId = (key: string, itemIndex?: number) =>
  itemIndex === undefined ? key : `${key}#${itemIndex}`;

// Name for a new checkbox/radio option. The builder used to write a literal
// 'BOX-NAME' into every option of every group, which collided page-wide.
// Nothing keys off `name` any more (the fill-side CheckboxGroup matches on
// index), but there is no reason to keep minting duplicates.
export const itemName = (key: string, itemIndex: number) =>
  `${key.replace(/\s+/g, '')}-box-${itemIndex + 1}`;

// Build the draggable elements for the active page.
export const buildElements = (
  pageData: Record<string, any>,
  globalFontPt: number = DEFAULT_FONT_PT,
): BuilderElement[] => {
  if (!pageData) return [];
  const out: BuilderElement[] = [];

  Object.keys(pageData).forEach(key => {
    if (key === 'completed') return;
    const item = pageData[key];
    if (!item) return;

    if (key === SIGNATURE) {
      out.push({
        id: elementId(key),
        key,
        kind: 'signature',
        anchor: 'topleft',
        leftMm: num(item.left),
        topMm: num(item.top),
        fontPt: globalFontPt,
        wMm: Number(item.width) || SIGNATURE_W_MM,
        hMm: Number(item.height) || SIGNATURE_H_MM,
        label: 'Signature',
        text: 'Signature',
      });
      return;
    }

    if (item.inputType && Array.isArray(item.items)) {
      item.items.forEach((box: any, i: number) => {
        out.push({
          id: elementId(key, i),
          key,
          itemIndex: i,
          kind: 'checkbox',
          anchor: 'baseline',
          leftMm: num(box.left),
          topMm: num(box.top),
          fontPt: fontOf(box, globalFontPt),
          isRadio: item.inputType === 'radio',
          label: box.caption || item.label || key,
          text: `${box.text ?? ''}`,
        });
      });
      return;
    }

    if (item.matrix) {
      out.push({
        id: elementId(key),
        key,
        kind: 'matrix',
        anchor: 'baseline',
        leftMm: num(item.matrixLeft),
        topMm: num(item.matrixTop),
        fontPt: fontOf(item, globalFontPt),
        cols: Math.max(1, num(item.columns)),
        rows: Math.max(1, num(item.rows)),
        gapHMm: num(item.gapHorizontal),
        gapVMm: num(item.gapVertical),
        colWidthsMm: columnWidthsOf(item),
        rowHeightsMm: rowHeightsOf(item),
        label: item.label || key,
        text: `▦ ${key}`,
      });
      return;
    }

    out.push({
      id: elementId(key),
      key,
      kind: 'field',
      anchor: 'baseline',
      leftMm: num(item.left),
      topMm: num(item.top),
      fontPt: fontOf(item, globalFontPt),
      wMm: Number(item.width) > 0 ? Number(item.width) : undefined,
      multiline: !!item.multiline,
      maxLines: Math.max(1, Number(item.maxLines) || DEFAULT_MAX_LINES),
      label: item.label || key,
      // A constant static mapping shows its real value on the canvas rather
      // than the '-test' placeholder, matching what Preview PDF now prints.
      text: (item.isStatic && item.fieldName !== 'NOW'
        ? resolveStaticValue({}, item.fieldName)
        : '') || item.text || key,
    });
  });

  return out;
};

// Return a new `data` object with the given element moved to (leftMm, topMm).
export const moveElement = (
  data: Record<string, any>,
  tab: string,
  el: BuilderElement,
  leftMm: number,
  topMm: number,
): Record<string, any> => {
  const left = `${roundMm(leftMm)}`;
  const top = `${roundMm(topMm)}`;
  const field = data[tab][el.key];

  if (el.kind === 'checkbox' && el.itemIndex !== undefined) {
    return {
      ...data,
      [tab]: {
        ...data[tab],
        [el.key]: {
          ...field,
          items: field.items.map((box: any, i: number) =>
            i === el.itemIndex ? { ...box, left, top } : box),
        },
      },
    };
  }

  if (el.kind === 'matrix') {
    return {
      ...data,
      [tab]: {
        ...data[tab],
        [el.key]: {
          ...field,
          matrixLeft: left,
          matrixTop: top,
          matrix: rebuildMatrix({ ...field, matrixLeft: left, matrixTop: top }),
        },
      },
    };
  }

  // field + signature
  return {
    ...data,
    [tab]: {
      ...data[tab],
      [el.key]: { ...field, left, top },
    },
  };
};

export interface ResizePayload {
  fontPt?: number;
  wMm?: number;
  hMm?: number;
  gapHMm?: number;
  gapVMm?: number;
  totalWMm?: number; // matrix only: new full width, scales every column
  totalHMm?: number; // matrix only: new full height, scales every row
  widthMm?: number; // text-field box width (for shrink-to-fit)
}

// Apply a resize: font size for text/checkbox, box for signature, grid spacing
// for a table.
export const resizeElement = (
  data: Record<string, any>,
  tab: string,
  el: BuilderElement,
  p: ResizePayload,
): Record<string, any> => {
  const field = data[tab][el.key];
  const put = (patch: Record<string, any>) => ({
    ...data,
    [tab]: { ...data[tab], [el.key]: { ...field, ...patch } },
  });

  if (el.kind === 'signature') {
    return put({ width: `${roundMm(p.wMm || 0)}`, height: `${roundMm(p.hMm || 0)}` });
  }

  if (el.kind === 'matrix') {
    // Columns may differ in width, so a corner drag scales every track by the
    // same factor instead of forcing one uniform gap and flattening the grid
    // the admin just lined up with the printed table.
    const cols = columnWidthsOf(field);
    const rows = rowHeightsOf(field);
    const nextCols = p.totalWMm ? scaleSizes(cols, p.totalWMm / (totalOf(cols) || 1)) : cols;
    const nextRows = p.totalHMm ? scaleSizes(rows, p.totalHMm / (totalOf(rows) || 1)) : rows;
    const next = {
      ...field,
      columnWidths: roundSizes(nextCols),
      rowHeights: roundSizes(nextRows),
    };
    return put({ ...next, matrix: rebuildMatrix(next) });
  }

  if (el.kind === 'checkbox' && el.itemIndex !== undefined) {
    return put({
      items: field.items.map((box: any, i: number) =>
        i === el.itemIndex ? { ...box, fontSize: `${p.fontPt}` } : box),
    });
  }

  // field: dragging the right edge sets the box width; corner/bottom sets font.
  if (p.widthMm !== undefined) {
    return put({ width: `${roundMm(p.widthMm)}` });
  }
  return put({ fontSize: `${p.fontPt}` });
};

// Move every box of a checkbox group by the same delta (used by keyboard nudge
// when a whole group is selected).
export const moveGroupBy = (
  data: Record<string, any>,
  tab: string,
  key: string,
  dLeftMm: number,
  dTopMm: number,
): Record<string, any> => {
  const field = data[tab][key];
  return {
    ...data,
    [tab]: {
      ...data[tab],
      [key]: {
        ...field,
        items: field.items.map((box: any) => ({
          ...box,
          left: `${roundMm(num(box.left) + dLeftMm)}`,
          top: `${roundMm(num(box.top) + dTopMm)}`,
        })),
      },
    },
  };
};
